import React from "react";
import { setIn } from "lodash-redux-immutability";
import {
  Container,
  Row,
  Column,
  Select,
  Option,
  Dd,
  Dt,
  Dl,
  Ul,
  Li,
  InputCheckbox,
  InputNumber,
} from "./Settings.styles";
import { Modal } from "components/Modal";
import {
  SettingsState,
  pageTitlePositions,
  backBehaviours,
} from "reducers/settings";
import { buttonPressTypes } from "services/keyListener";

export type Props = {
  open: boolean;
  settings: SettingsState;
  onUpdateSettings: (settings: Partial<SettingsState>) => void;
  onClose?: () => void;
};

const Dropdown = <
  Value extends string | number,
  Options extends Readonly<Value[]>
>({
  value,
  options,
  onChange,
  inline,
}: {
  value: Value;
  options: Options;
  onChange: (val: Value) => void;
  inline?: boolean;
}) => (
  <Select
    inline={inline}
    value={value}
    onChange={evt => onChange(evt.target.value as Options[number])}
  >
    {options.map(val => (
      <Option key={val} value={val}>
        {val}
      </Option>
    ))}
  </Select>
);

const Checkbox = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (val: boolean) => void;
}) => (
  <InputCheckbox
    type="checkbox"
    checked={value}
    onChange={evt => onChange(evt.target.checked)}
  />
);

export const Settings = ({
  open,
  settings,
  onUpdateSettings,
  onClose,
}: Props) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Container>
        <h2>Settings</h2>
        <Row>
          <Column>
            <h3>List of Key Commands</h3>
            <Row>
              <Dl>
                <Dt>&larr;</Dt>
                <Dd>Left</Dd>
                <Dt>&rarr;</Dt>
                <Dd>Right</Dd>
                <Dt>&uarr;</Dt>
                <Dd>Up</Dd>
                <Dt>&darr;</Dt>
                <Dd>Down</Dd>
                <Dt>Enter</Dt>
                <Dd>Select</Dd>
                <Dt>h</Dt>
                <Dd>Home</Dd>
                <Dt>b</Dt>
                <Dd>Back</Dd>
                <Dt>u</Dt>
                <Dd>Volume Up</Dd>
                <Dt>d</Dt>
                <Dd>Volume Down</Dd>
                <Dt>a</Dt>
                <Dd>Add to My List</Dd>
                <Dt>s</Dt>
                <Dd>Show/hide this settings modal</Dd>
              </Dl>
              <Dl>
                <Dt>1</Dt>
                <Dd>Voice wake</Dd>
                <Dt>2</Dt>
                <Dd>Go to TV shows</Dd>
                <Dt>3</Dt>
                <Dd>Play the Reluctant Landlord</Dd>
                <Dt>4</Dt>
                <Dd>Volume down by 50</Dd>
                <Dt>5</Dt>
                <Dd>Mute the audio</Dd>
                <Dt>6</Dt>
                <Dd>Unmute the audio</Dd>
                <Dt>7</Dt>
                <Dd>Go back</Dd>
                <Dt>8</Dt>
                <Dd>Go back</Dd>
                <Dt>9</Dt>
                <Dd>Go back</Dd>
              </Dl>
            </Row>
          </Column>
          <Column>
            <h3>Example Voice Commands</h3>
            <Ul>
              <Li>"Hello Sky" - wake and listen to further commands</Li>
              <Li>
                "Go to TV shows", "Go to the TV page", "Open the Movies page"
                and similar
              </Li>
              <Li>
                "Turn the volume down", "Volume up", "Volume down by 20",
                "quieter", etc.
              </Li>
              <Li>"Mute", "unmute", "mute the volume", "be quiet", etc.</Li>
              <Li>"Play the Reluctant Landlord", "Play The Capture", etc.</Li>
              <Li>"Go back"</Li>
            </Ul>
          </Column>
        </Row>
        <Row>
          <Column>
            <h3>Page Title position</h3>
            <Dropdown
              value={settings.pageTitlePosition}
              options={pageTitlePositions}
              onChange={pageTitlePosition =>
                onUpdateSettings({ pageTitlePosition })
              }
            />
          </Column>
          <Column>
            <h3>Back behaviour</h3>
            <Dropdown
              value={settings.backBehaviour}
              options={backBehaviours}
              onChange={backBehaviour => onUpdateSettings({ backBehaviour })}
            />
          </Column>
          <Column>
            <h3>Enable Browse Tabs</h3>
            <Checkbox
              value={settings.enableBrowseTabs}
              onChange={enableBrowseTabs =>
                onUpdateSettings({ enableBrowseTabs })
              }
            />
          </Column>
        </Row>
        <h3>Remote control button press timings</h3>
        <Row>
          {settings.buttonPressTiers.map(({ after, every, pressType }, i) => (
            <Column key={i}>
              <h3>Tier {i + 1}</h3>
              After
              <InputNumber
                inline={true}
                min="0"
                type="number"
                value={after}
                onChange={e =>
                  e.target.value &&
                  onUpdateSettings(
                    setIn(
                      settings,
                      ["buttonPressTiers", i, "after"],
                      parseFloat(e.target.value)
                    )
                  )
                }
              />
              seconds of first pressing,
              <br />
              trigger a
              <Dropdown
                inline={true}
                value={pressType as string}
                options={buttonPressTypes}
                onChange={pressType =>
                  onUpdateSettings(
                    setIn(
                      settings,
                      ["buttonPressTiers", i, "pressType"],
                      pressType
                    )
                  )
                }
              />
              press
              <br />
              every
              <InputNumber
                inline={true}
                min="0"
                type="number"
                value={every}
                onChange={e =>
                  e.target.value &&
                  onUpdateSettings(
                    setIn(
                      settings,
                      ["buttonPressTiers", i, "every"],
                      parseFloat(e.target.value)
                    )
                  )
                }
              />
              seconds.
            </Column>
          ))}
        </Row>
      </Container>
    </Modal>
  );
};
